"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type * as React from "react";
import { cn } from "@/lib/utils";

export interface PageProps extends useRender.ComponentProps<"div"> {
  /** Inner content max-width in px (centered). Defaults to 1080. */
  max?: number;
}

/**
 * Calm Authority page content wrapper. Ports docs/design-system/templates/app/
 * layout.jsx `Page`: `30px 34px 80px` padding with a centered max-width column.
 * Horizontal padding relaxes to 16px below the `sm` breakpoint so narrow
 * viewports do not lose content width.
 */
export function Page({
  className,
  max = 1080,
  style,
  render,
  children,
  ...props
}: PageProps): React.ReactElement {
  const defaultProps = {
    children: (
      <div className="mx-auto" style={{ maxWidth: max }}>
        {children}
      </div>
    ),
    className: cn("px-4 pt-[30px] pb-20 sm:px-[34px]", className),
    "data-slot": "page",
    style,
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}
